import { AsyncPipe, NgClass } from '@angular/common';
import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { catchError, EMPTY, Observable, of, tap } from 'rxjs';

import { UaService } from '@ecm/consumer';

import { MaterialCaptcha } from '../../business/material-captcha-model/material-captcha';
import { MaterialCaptchaParameters } from '../../business/material-captcha-model/material-captcha-parameters';
import { MaterialCaptchaSelection } from '../../business/material-captcha-model/material-captcha-selection';
import { CaptchaService } from '../../services/captcha.service';
import { MaterialSpinnerComponent } from '../material-spinner/material-spinner.component';
import { AbstractComponent } from '../shared/abstract.component';
import { MaterialCaptchaStepsComponent } from './material-captcha-step/material-captcha-steps.component';

@Component({
    selector: 'ecm-mat-captcha',
    templateUrl: './material-captcha.component.html',
    styleUrls: ['./material-captcha.component.css'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [MaterialCaptchaStepsComponent, NgClass, MaterialSpinnerComponent, AsyncPipe, TranslateModule],
})
export class MaterialCaptchaComponent extends AbstractComponent implements OnInit {
    /**
     * i18n root path
     * @type {string}
     */
    public i18nPath = 'material.captcha.links';

    /**
     * submit button label may be provided if needed
     * @type {string} submitButtonLabel
     */
    @Input() public submitButtonLabel?: string;

    /**
     * erase button label may be provided if needed
     * @type {string} eraseButtonLabel
     */
    @Input() public eraseButtonLabel?: string;

    /**
     * captcha error message to show
     * @type {string}
     */
    @Input()
    public errorMessageLabel: string;

    /**
     * Parent form is complete
     * @type {boolean}
     */
    @Input()
    public parentFormComplete: boolean;

    /**
     * show error message
     * @type {boolean}
     */
    @Input()
    public captchaError: boolean;

    /**
     * (Optional) URL to the service called for GET captcha. Should be specific to each feature
     * @type {string}
     */
    @Input()
    public getCaptchaUrl?: string;

    /**
     * (Optional) captcha data. Should be specific to each feature
     * @type {MaterialCaptcha}
     */
    @Input()
    public captchaData?: MaterialCaptcha;

    /**
     * send error status to parent
     * @type {EventEmitter<boolean>}
     */
    @Output()
    public captchaErrorChange = new EventEmitter();

    /**
     * Emit event when parent need to hide captcha
     * ie : client is logged or a advisor
     * @type {EventEmitter<boolean>}
     */
    @Output()
    // eslint-disable-next-line @angular-eslint/no-output-on-prefix
    public onCaptchaUnavailable = new EventEmitter<boolean>();

    /**
     * Emit event end selection
     * @type {EventEmitter<MaterialCaptchaParameters>}
     */
    @Output()
    // eslint-disable-next-line @angular-eslint/no-output-on-prefix
    public onCaptchaComplete = new EventEmitter<MaterialCaptchaParameters>();

    /**
     * selections customer
     * @type {MaterialCaptchaSelection[]}
     */
    public selections: MaterialCaptchaSelection[] = [];

    /**
     * values captcha
     * @type {Observable<MaterialCaptcha>}
     */
    public captcha$: Observable<MaterialCaptcha>;

    /**
     * Indications captcha
     * @type {string[]}
     */
    public indications: string[] = [];

    public constructor(
        protected uaService: UaService,
        public captchaService: CaptchaService,
    ) {
        super(uaService);
    }

    public ngOnInit(): void {
        super.init();
        this.initCaptcha();
    }

    /**
     * Refresh captcha
     */
    public refreshCaptcha(): void {
        this.initCaptcha();
    }

    /**
     * add customer choice to selections and set values
     * @param {string[]} indications
     * @param {string} imgName
     * @param {number} value
     */
    public addChoice(indications: string[], imgName: string, value: number): void {
        this.captchaErrorChange.emit(false);
        this.selections.push({ img: imgName, value });
        if (indications.length > this.indications.length) {
            this.indications.push(indications[this.indications.length]);
        }
    }

    /**
     * Emit even on submit
     */
    public sendCaptcha(captcha: MaterialCaptcha): void {
        const captchaValue: string = this.selections.map((selection: any) => selection.value).join('-') + '-';
        this.onCaptchaComplete.emit({
            urlNext: captcha.next,
            signature: captcha.signature,
            hash: captcha.hash,
            value: captchaValue,
        });
    }

    /**
     * delete all customer selections
     */
    public deleteSelections(indications: string[]): void {
        this.initIndications(indications);
        this.selections = [];
    }

    /**
     * customer finish selection
     * @returns {boolean}
     */
    public selectionDone(indications: string[]): boolean {
        return indications.length === this.selections.length;
    }

    /**
     * captcha selection in progress
     * @returns {boolean}
     */
    public selectionInProgress(): boolean {
        return this.selections.length >= 1;
    }

    /**
     * disable submit button if parent form is not complete
     * @returns {boolean}
     */
    public disableSubmitButton(indications: string[]): boolean {
        return !(this.parentFormComplete && this.selectionDone(indications));
    }

    /**
     * Init and generate captcha
     */
    private initCaptcha(): void {
        this.selections = [];
        this.indications = [];

        if (this.captchaData) {
            this.captcha$ = of(this.captchaData);
            this.initIndications(this.captchaData.indications);
        } else {
            this.captcha$ = this.captchaService.getCaptcha(this.getCaptchaUrl).pipe(
                tap((captcha) => this.initIndications(captcha.indications)),
                catchError(() => {
                    this.captchaError = true;
                    this.onCaptchaUnavailable.emit(true);
                    return EMPTY;
                }),
            );
        }
    }

    /**
     * Initialize indications
     */
    private initIndications(indications: string[]): void {
        this.indications = this.isUserAgentMobile ? [indications[0]] : indications;
    }
}
